import {ZoomIn, ZoomOut} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {FILTER_BAR_COMPACT_ONLY, FILTER_BAR_FULL_ONLY} from '@/lib/filterBar';
import {cn} from '@/lib/utils';

type ThumbnailSizeControlProps = Readonly<{
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  sliderClassName?: string;
}>;

// Slider flanked by decrease/increase buttons, so the magnifier icons do what they look like.
export function ThumbnailSizeControl({
  min,
  max,
  value,
  onChange,
  sliderClassName = 'w-24'
}: ThumbnailSizeControlProps) {
  return (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Decrease thumbnail size"
              disabled={value <= min}
              onClick={() => {
                onChange(Math.max(min, value - 1));
              }}
            />
          }>
          <ZoomOut className="text-muted-foreground size-3.5" />
        </TooltipTrigger>
        <TooltipContent>Decrease thumbnail size</TooltipContent>
      </Tooltip>
      <div className={sliderClassName}>
        <Slider
          min={min}
          max={max}
          step={1}
          value={value}
          getAriaLabel={() => 'Thumbnail size'}
          onValueChange={(next) => {
            const level = typeof next === 'number' ? next : next[0];
            if (typeof level === 'number') {
              onChange(level);
            }
          }}
        />
      </div>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Increase thumbnail size"
              disabled={value >= max}
              onClick={() => {
                onChange(Math.min(max, value + 1));
              }}
            />
          }>
          <ZoomIn className="text-muted-foreground size-3.5" />
        </TooltipTrigger>
        <TooltipContent>Increase thumbnail size</TooltipContent>
      </Tooltip>
    </div>
  );
}

// The size control as the filter bar wants it: the full slider while the bar is wide, a single
// zoom button opening the same control in a menu once it goes compact (lib/filterBar.ts). Both
// are rendered and CSS picks one, so the break follows the bar's own width, not the window's.
export function ResponsiveThumbnailSizeControl(props: ThumbnailSizeControlProps) {
  return (
    <>
      <div className={FILTER_BAR_FULL_ONLY}>
        <ThumbnailSizeControl {...props} />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Thumbnail size"
              title="Thumbnail size"
              className={cn('shrink-0', FILTER_BAR_COMPACT_ONLY)}
            />
          }>
          <ZoomIn />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="p-2">
          <ThumbnailSizeControl {...props} sliderClassName="w-32" />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
